/**
 * Helper functions and components for CompanyInfoStep
 */

// Helper to format date to YYYY-MM-DD without timezone issues
export function formatDateToString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Smart PAN input handler with position-aware validation
 * PAN Format: AAAAA9999A (5 letters, 4 digits, 1 letter)
 */
export function validatePANInput(value: string): string {
  const input = value.toUpperCase().replace(/[^A-Z0-9]/g, '') // Remove spaces and special chars
  let validated = ''

  for (let i = 0; i < Math.min(input.length, 10); i++) {
    const char = input[i]
    if (!char) {continue}
    if (i < 5 || i === 9) {
      // Positions 0-4 and 9: letters only
      if (/[A-Z]/.test(char)) {validated += char}
    } else {
      // Positions 5-8: digits only
      if (/[0-9]/.test(char)) {validated += char}
    }
  }

  return validated
}

/**
 * Smart GSTIN input handler with position-aware validation
 * GSTIN Format: NNAAAAANNNNANAX (15 characters)
 */
/** Returns true when the character is valid at the given GSTIN position. */
function isValidGSTINCharAtPosition(char: string, position: number): boolean {
  const isDigitPosition = position < 2 || (position >= 7 && position <= 10) || position === 12
  const isChecksumPosition = position === 14
  if (isDigitPosition) { return /[0-9]/.test(char) }
  if (isChecksumPosition) { return /[A-Z0-9]/.test(char) }
  return /[A-Z]/.test(char)
}

export function validateGSTINInput(value: string): { gstin: string; extractedPAN?: string } {
  const input = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
  let validated = ''

  for (let i = 0; i < Math.min(input.length, 15); i++) {
    const char = input[i]
    if (char && isValidGSTINCharAtPosition(char, i)) { validated += char }
  }

  if (validated.length >= 12) {
    return { gstin: validated, extractedPAN: validated.substring(2, 12) }
  }

  return { gstin: validated }
}

/**
 * Handle paste events for PAN/GSTIN - clean and validate
 */
export function handleBusinessIdPaste(
  e: React.ClipboardEvent<HTMLInputElement>,
  type: 'pan' | 'gstin'
): { gstin?: string; pan?: string } {
  e.preventDefault()
  const pastedText = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '')

  if (type === 'pan') {
    return { pan: validatePANInput(pastedText) }
  } else {
    const result = validateGSTINInput(pastedText)
    if (result.extractedPAN) {
      return { gstin: result.gstin, pan: result.extractedPAN }
    }
    return { gstin: result.gstin }
  }
}

/**
 * Smart email input handler - removes invalid characters
 * Allows: a-z, 0-9, @, ., -, _, +
 */
export function cleanEmailInput(value: string): string {
  // Remove spaces and convert to lowercase
  // Only allow valid email characters: letters, numbers, @, ., -, _, +
  return value
    .toLowerCase()
    .replace(/\s/g, '') // Remove all whitespace
    .replace(/[^a-z0-9@._+-]/g, '') // Remove invalid characters
}

/**
 * Handle email paste - clean invalid characters
 */
export function handleEmailPaste(e: React.ClipboardEvent<HTMLInputElement>): string {
  e.preventDefault()
  return cleanEmailInput(e.clipboardData.getData('text'))
}

interface ValidationErrors {
  name?: string
  fiscalYearStart?: string
  fiscalYearEnd?: string
  email?: string
  website?: string
  pan?: string
  gstin?: string
}

interface CompanyInfo {
  name: string
  fiscalYearStart: string
  fiscalYearEnd: string
  email?: string
  website?: string
  pan?: string
  gstin?: string
}

function validateFiscalYearDates(
  fiscalYearStart: string,
  fiscalYearEnd: string,
  errors: ValidationErrors
): void {
  const start = new Date(fiscalYearStart)
  const end = new Date(fiscalYearEnd)
  if (end <= start) {
    errors.fiscalYearEnd = 'End date must be after start date'
    return
  }
  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 360 || diffDays > 370) {
    errors.fiscalYearEnd = 'Fiscal year should be approximately 12 months'
  }
}

function validateOptionalFields(info: CompanyInfo, errors: ValidationErrors): void {
  if (info.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) {
    errors.email = 'Please enter a valid email address'
  }
  if (info.website && !/^https?:\/\/.+/.test(info.website)) {
    errors.website = 'Please enter a valid URL (starting with http:// or https://)'
  }
  if (info.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(info.pan)) {
    errors.pan = 'Invalid PAN format. Must be 10 characters: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)'
  }
  if (info.gstin && info.gstin.length !== 15) {
    errors.gstin = 'GSTIN must be exactly 15 characters'
  }
}

export function validateCompanyInfo(info: CompanyInfo): ValidationErrors {
  const errors: ValidationErrors = {}

  if (!info.name.trim()) { errors.name = 'Company name is required' }
  if (!info.fiscalYearStart) { errors.fiscalYearStart = 'Fiscal year start date is required' }
  if (!info.fiscalYearEnd) { errors.fiscalYearEnd = 'Fiscal year end date is required' }

  if (info.fiscalYearStart && info.fiscalYearEnd) {
    validateFiscalYearDates(info.fiscalYearStart, info.fiscalYearEnd, errors)
  }

  validateOptionalFields(info, errors)

  return errors
}

export function focusFirstError(errors: ValidationErrors): void {
  // If there are errors, focus on the first field with an error
  if (Object.keys(errors).length > 0) {
    // Wait for errors to be rendered in the DOM
    setTimeout(() => {
      const firstErrorField = Object.keys(errors)[0]
      const errorElement = document.querySelector(`[name="${firstErrorField}"], [id="${firstErrorField}"]`) as HTMLElement

      if (errorElement) {
        // Scroll to the element with some offset for better visibility
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // Focus the element
        errorElement.focus()
      }
    }, 100)
  }
}
